import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { assertReportAdmission, NativeConditionError, validateConditionalDefinition, type AdmissionOperation } from "../nativeConditions.mjs";
import { resolveNativeLayoutPlan } from "../nativeLayout.mjs";
import { createInternalFormattingExecution } from "../nativeVisibility.mjs";
import { asRecord, cloneValue, type DesignerDocument } from "./reportDesignerModel";
import { formulaDesignProjection, isInternalFormulaSession, validateInternalFormulaSource, type InternalFormulaSession } from "./internalFormulaSession";

const sessions = new WeakSet<InternalConditionalSession>();
const owners = new WeakMap<object, InternalDesignerSession>();

export type InternalConditionalSample = { id: string; label: string; datasets: FormattedInvoicePayload["datasets"] };
export type InternalConditionalSession = Readonly<{ initialPayload: FormattedInvoicePayload; samples: readonly InternalConditionalSample[] }>;
export type InternalDesignerSession = InternalConditionalSession | InternalFormulaSession;

export function createInternalConditionalSession(payload: FormattedInvoicePayload, samples: InternalConditionalSample[] = []): InternalConditionalSession {
  if (process.env.NODE_ENV === "production") throw new Error("Internal conditional authoring is unavailable in production.");
  validateInternalSource(payload);
  samples.forEach(sample => validateInternalSource({ ...payload, datasets: sample.datasets }));
  const session = Object.freeze({ initialPayload: cloneValue(payload), samples: cloneValue(samples) });
  sessions.add(session);
  return session;
}

function validateInternalSource(value: unknown) {
  const admission = assertReportAdmission(value, "validate");
  if (admission.requiredFeatures.some(feature => feature !== "conditional-rules-v1")) throw new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Internal conditional authoring does not admit typed expressions.");
  if (asRecord(value).schemaVersion !== "reports-v3.1") throw new Error("Internal conditional authoring requires a native reports-v3.1 fixture.");
  if (asRecord(value).renderer !== undefined && asRecord(value).sourceSchemaVersion !== "reports-v3.1") throw new Error("Internal normalized documents must retain their native reports-v3.1 source discriminator.");
  const result = validateConditionalDefinition(value);
  if (result.errors.length) {
    const error = result.errors[0];
    throw new NativeConditionError(error.code, error.path, error.message, error);
  }
}

export function admitInternalSource(value: object, session: InternalDesignerSession) {
  if (process.env.NODE_ENV === "production" || (!isInternalFormulaSession(session) && !sessions.has(session))) throw new Error("Invalid internal authoring session.");
  if (isInternalFormulaSession(session)) validateInternalFormulaSource(value, session);
  else validateInternalSource(value);
  owners.set(value, session);
}

export function internalSessionOf(value: object): InternalDesignerSession | undefined {
  return process.env.NODE_ENV === "production" ? undefined : owners.get(value);
}

export function inheritInternalSession(source: object, next: object) {
  const session = internalSessionOf(source);
  if (session) owners.set(next, session);
}

export function assertDesignerCommandAdmission(document: DesignerDocument) {
  const session = internalSessionOf(document);
  if (isInternalFormulaSession(session)) validateInternalFormulaSource(document, session);
  else if (session) validateInternalSource(document);
  else assertReportAdmission(document, "edit");
}

export function assertDesignerAdapterAdmission(value: object, operation: AdmissionOperation, session?: InternalDesignerSession) {
  if (session) {
    if (operation === "serialize" && internalSessionOf(value) !== session) throw new Error("The document does not belong to this internal session.");
    admitInternalSource(value, session);
  }
  else assertReportAdmission(value, operation);
}

/** A Design-only projection: retain rules in the source; do not evaluate them on the editing surface. */
export function internalDesignProjection(payload: FormattedInvoicePayload): FormattedInvoicePayload {
  const session = internalSessionOf(payload);
  if (!session) return payload;
  if (isInternalFormulaSession(session)) return formulaDesignProjection(payload);
  const next = cloneValue(payload);
  function visit(value: unknown) {
    if (!value || typeof value !== "object") return;
    const record = asRecord(value);
    const conditions = asRecord(record.conditions);
    if (conditions.visibleWhen) asRecord(conditions.visibleWhen).enabled = false;
    if (Array.isArray(conditions.formatting)) conditions.formatting.forEach(rule => { asRecord(rule).enabled = false; });
    Object.values(value).forEach(visit);
  }
  visit(next.layout);
  inheritInternalSession(payload, next);
  return next;
}

export function resolveDesignerLayout(payload: FormattedInvoicePayload) {
  if (!internalSessionOf(payload)) return resolveNativeLayoutPlan(payload);
  if (isInternalFormulaSession(internalSessionOf(payload))) return resolveNativeLayoutPlan(formulaDesignProjection(payload));
  const projection = internalDesignProjection(payload);
  return resolveNativeLayoutPlan(projection, { visibilityExecution: createInternalFormattingExecution(projection) });
}

export function internalSamplePayload(payload: FormattedInvoicePayload, session: InternalDesignerSession, index: number) {
  if (internalSessionOf(payload) !== session) throw new Error("Sample input requires the owning internal session.");
  const sample = session.samples[index];
  if (!sample) return payload;
  const next = { ...payload, datasets: cloneValue(sample.datasets) };
  inheritInternalSession(payload, next);
  return next;
}
